import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";

export default class CalculationWizardProductInfoController extends Controller {
  static targets = [
    "productInput",
    "productsList",
    "totalAnnualProductionInput",
    "unitOfProductionSelect",
    "addButton",
    "toggleButton",
    "toggleIcon",
    "toggleText",
    "commonProducts",
    "searchDropdown",
    "searchStatus",
    "searchResults",
  ];

  static values = {
    calculationId: Number,
  };

  connect() {
    this.selectedProductIds = new Set();
    this.commonProductsVisible = false;
    this.allProducts = []; // Store all available products for search
    this.searchTimeout = null;
    this.productsLoaded = false; // Track if products have been loaded

    // Initialize with existing products if any
    this.initializeExistingProducts();

    // Load common products
    this.loadCommonProducts();

    // Trigger validation when products list changes
    if (this.hasProductsListTarget) {
      this.productsListTarget.addEventListener("DOMNodeInserted", () =>
        this._triggerValidation()
      );
      this.productsListTarget.addEventListener("DOMNodeRemoved", () =>
        this._triggerValidation()
      );
    }

    // Setup click outside handler for search dropdown
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  initializeExistingProducts() {
    if (this.hasProductsListTarget) {
      const productElements =
        this.productsListTarget.querySelectorAll("[data-product-id]");
      productElements.forEach((el) => {
        this.selectedProductIds.add(el.dataset.productId);
      });
    }
  }

  handleProductKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.addProduct();
    }
  }

  validateProductInput() {
    const productName = this.productInputTarget.value.trim();
    this.addButtonTarget.disabled = !productName;

    // Trigger search with debounce
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(productName);
    }, 300);
  }

  // Method called by template
  searchProducts() {
    this.validateProductInput();
  }

  // Method called by template
  showDropdown() {
    const query = this.productInputTarget.value.trim();
    if (query.length >= 2) {
      this.performSearch(query);
    } else {
      this.showSearchDropdown([], query);
    }
  }

  // Method called by template
  hideDropdown() {
    // Add a small delay to allow clicks on dropdown items
    setTimeout(() => {
      this.hideSearchDropdown();
    }, 150);
  }

  // Method called by template
  addSelectedProduct() {
    this.addProduct();
  }

  performSearch(query) {
    if (!query || query.length < 2) {
      this.hideSearchDropdown();
      return;
    }

    // Filter products based on query
    const filteredProducts = this.allProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(query.toLowerCase()) &&
        !this.selectedProductIds.has(product.id.toString())
    );

    this.showSearchDropdown(filteredProducts, query);
  }

  showSearchDropdown(products, query) {
    if (!this.hasSearchDropdownTarget) return;

    this.searchDropdownTarget.classList.remove("hidden");

    if (products.length === 0) {
      this.searchStatusTarget.textContent = query
        ? `No products found matching "${query}"`
        : "Start typing to search products...";
      this.searchResultsTarget.innerHTML = "";
      return;
    }

    this.searchStatusTarget.textContent = "";
    this.searchResultsTarget.innerHTML = products
      .map(
        (product) => `
        <button type="button"
                class="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-900"
                data-product-id="${product.id}"
                data-product-name="${product.name}"
                data-action="click->calculation-wizard-product-info#selectSearchProduct">
          ${product.name}
        </button>
      `
      )
      .join("");
  }

  hideSearchDropdown() {
    if (this.hasSearchDropdownTarget) {
      this.searchDropdownTarget.classList.add("hidden");
    }
  }

  selectSearchProduct(event) {
    const productId = event.target.dataset.productId;
    const productName = event.target.dataset.productName;

    if (!this.selectedProductIds.has(productId)) {
      // Create the association via API
      const locale = document.documentElement.lang || "en";
      httpRequest(
        `/${locale}/wage-calculations/wizard-api/${this.calculationIdValue}/products`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            product: {
              id: productId,
              name: productName,
            },
          }),
        }
      )
        .then(() => {
          // Add to UI after successful API call
          this.selectedProductIds.add(productId.toString());
          this._addProductToList(productId, productName);
          this._triggerValidation();
        })
        .catch((error) => {
          console.error("Error adding product:", error);
        });
    }

    // Clear input and hide dropdown
    this.productInputTarget.value = "";
    this.addButtonTarget.disabled = true;
    this.hideSearchDropdown();
  }

  toggleCommonProducts() {
    this.commonProductsVisible = !this.commonProductsVisible;

    if (this.hasCommonProductsTarget) {
      if (this.commonProductsVisible) {
        this.commonProductsTarget.classList.remove("hidden");
        this.toggleIconTarget.classList.add("rotate-180");
        this.toggleTextTarget.textContent = this._t(
          "calculation_wizard.addons.hide_common_products",
          "Hide common products"
        );
      } else {
        this.commonProductsTarget.classList.add("hidden");
        this.toggleIconTarget.classList.remove("rotate-180");
        this.toggleTextTarget.textContent = this._t(
          "calculation_wizard.addons.show_common_products",
          "Show common products"
        );
      }
    }
  }

  renderCommonProducts(products) {
    if (!this.hasCommonProductsTarget) return;

    const container = this.commonProductsTarget.querySelector(".grid");
    if (!container) return;

    container.innerHTML = products
      .filter((product) => !this.selectedProductIds.has(product.id.toString()))
      .map(
        (product) => `
        <button type="button"
                class="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 hover:border-brand-600 hover:bg-brand-25 transition-colors"
                data-product-id="${product.id}"
                data-product-name="${product.name}"
                data-action="click->calculation-wizard-product-info#selectCommonProduct">
          ${product.name}
        </button>
      `
      )
      .join("");
  }

  selectCommonProduct(event) {
    event.preventDefault();
    const productId = event.target.dataset.productId;
    const productName = event.target.dataset.productName;

    if (!this.selectedProductIds.has(productId)) {
      // Create the association via API
      const locale = document.documentElement.lang || "en";
      httpRequest(
        `/${locale}/wage-calculations/wizard-api/${this.calculationIdValue}/products`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            product: {
              id: productId,
              name: productName,
            },
          }),
        }
      )
        .then(() => {
          // Add to UI after successful API call
          this.selectedProductIds.add(productId.toString());
          this._addProductToList(productId, productName);
          this.renderCommonProducts(this.allProducts);
          this._triggerValidation();
          this.updateParentSectionHeight();
        })
        .catch((error) => {
          console.error("Error adding product:", error);
        });
    }
  }

  loadCommonProducts() {
    // Get the facility from the calculation
    const facilitySelect = document.querySelector(
      '[data-calculation-wizard-basic-info-target="facilitySelect"]'
    );
    let facilityId = facilitySelect ? facilitySelect.value : "";

    if (!facilityId) {
      // Try to get from a data attribute on the form
      const form = this.element.closest("form");
      if (form && form.dataset.facilityId) {
        facilityId = form.dataset.facilityId;
      }
    }

    if (!facilityId) {
      if (this.hasCommonProductsTarget) {
        this.commonProductsTarget.innerHTML =
          '<p class="text-sm text-gray-500">Select a facility to see common products</p>';
      }
      this.allProducts = [];
      this.productsLoaded = true;
      return;
    }

    // Fetch products for the selected facility
    const currentLocale = window.location.pathname.split("/")[1];
    const url = `/${currentLocale}/api/facilities/${facilityId}/products`;

    fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const products = data.products || [];
        this.allProducts = products;
        this.productsLoaded = true;
        this.renderCommonProducts(products);
      })
      .catch((error) => {
        console.error("Error loading products:", error);
        if (this.hasCommonProductsTarget) {
          this.commonProductsTarget.innerHTML =
            '<p class="text-sm text-gray-500">Error loading products</p>';
        }
        this.allProducts = [];
        this.productsLoaded = true;
      });
  }

  _t(_key, defaultValue) {
    // Simple translation helper - returns default value
    // In a real app, this would use the i18n system
    return defaultValue;
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.hasSearchDropdownTarget) {
      this.hideSearchDropdown();
    }
  }

  addProduct(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this.hasProductInputTarget) {
      console.error("Product input target not found");
      return;
    }

    const productName = this.productInputTarget.value.trim();

    if (!productName) {
      return;
    }

    // Check if product already exists in our list
    const existingProduct = this.allProducts.find(
      (p) => p.name.toLowerCase() === productName.toLowerCase()
    );

    // Always call API to persist the product association
    const locale = document.documentElement.lang || "en";
    const productData = existingProduct
      ? { id: existingProduct.id, name: existingProduct.name }
      : { name: productName };

    httpRequest(
      `/${locale}/wage-calculations/wizard-api/${this.calculationIdValue}/products`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product: productData,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        // Add the product to the list
        if (!this.selectedProductIds.has(data.id.toString())) {
          this.selectedProductIds.add(data.id.toString());
          this._addProductToList(data.id, data.name);
          this._triggerValidation();
          this.updateParentSectionHeight();
        }
      })
      .catch((error) => {
        console.error("Error adding product:", error);
      });

    // Clear the input
    this.productInputTarget.value = "";
    this.addButtonTarget.disabled = true;
    this.hideSearchDropdown();
  }

  removeProduct(event) {
    event.preventDefault();

    const productElement = event.target.closest("[data-product-id]");
    const productId = productElement.dataset.productId;

    // Remove via API
    const locale = document.documentElement.lang || "en";
    httpRequest(
      `/${locale}/wage-calculations/wizard-api/${this.calculationIdValue}/products/${productId}`,
      {
        method: "DELETE",
      }
    )
      .then(() => {
        // Remove from UI
        productElement.remove();
        this.updateParentSectionHeight();
      })
      .catch((error) => {
        console.error("Error removing product:", error);
      });
  }

  _addProductToList(productId, productName) {
    if (!this.hasProductsListTarget) {
      console.error("Products list target not found");
      return;
    }

    const productElement = document.createElement("span");
    productElement.className =
      "inline-flex items-center rounded-full bg-brand-25 pl-3 pr-1.5 py-1 text-sm text-brand-700";
    productElement.dataset.productId = productId;
    productElement.innerHTML = `
      ${productName}
      <button type="button" class="ml-[2px] text-gray-500 hover:text-red-600 transition-colors p-[2px]" data-action="click->calculation-wizard-product-info#removeProduct" title="Remove product">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M9 3L3 9M3 3L9 9" stroke="#468D8A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    `;
    this.productsListTarget.appendChild(productElement);
  }

  _triggerValidation() {
    // Dispatch a custom event to trigger validation in the addons controller
    const event = new CustomEvent("product-list-changed", { bubbles: true });
    this.element.dispatchEvent(event);
  }

  updateParentSectionHeight() {
    // Find the parent collapsible section
    const collapsibleSection = this.element.closest(
      '[data-controller*="collapsible-section"]'
    );
    if (collapsibleSection) {
      const bodyElement = collapsibleSection.querySelector(
        "[data-collapsible-section-target='body']"
      );
      if (bodyElement) {
        // Update max-height to accommodate new content
        requestAnimationFrame(() => {
          bodyElement.style.maxHeight = bodyElement.scrollHeight + "px";
        });
      }
    }
  }
}
